import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiResponse } from "@/types";
import type {
  IConnectApiTokenPayload,
  IIntegration,
  IIntegrationOption,
  IJiraProject,
} from "@/types/(company)/integrations.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

const INTEGRATIONS = APIEndpoints.Company.Integrations;

export const companyIntegrationsApi = createApi({
  reducerPath: "companyIntegrationsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["CompanyIntegrations"],
  endpoints: (builder) => ({
    companyGetIntegrations: builder.query<IApiResponse<IIntegration[]>, void>({
      query: () => ({ url: INTEGRATIONS.Root }),
      providesTags: ["CompanyIntegrations"],
    }),
    companyGetAvailableIntegrations: builder.query<
      IApiResponse<IIntegrationOption[]>,
      { provider?: string } | void
    >({
      query: (args) => ({
        url: INTEGRATIONS.Available,
        params: args?.provider ? { provider: args.provider } : undefined,
      }),
      providesTags: ["CompanyIntegrations"],
    }),
    // Lazy-only: fetching the URL is the first step of a full-page redirect.
    companyGetJiraAuthorizeUrl: builder.query<
      IApiResponse<{ url: string }>,
      void
    >({
      query: () => ({ url: INTEGRATIONS.JiraAuthorize }),
    }),
    companyConnectJiraApiToken: builder.mutation<
      IApiResponse<IIntegration>,
      IConnectApiTokenPayload
    >({
      query: (body) => ({
        url: INTEGRATIONS.ApiToken,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyIntegrations"],
    }),
    companyVerifyIntegration: builder.mutation<
      IApiResponse<IIntegration>,
      { id: number }
    >({
      query: ({ id }) => ({ url: INTEGRATIONS.Verify(id), method: "POST" }),
      invalidatesTags: ["CompanyIntegrations"],
    }),
    companyDeleteIntegration: builder.mutation<
      IApiResponse<unknown>,
      { id: number }
    >({
      query: ({ id }) => ({ url: INTEGRATIONS.ById(id), method: "DELETE" }),
      invalidatesTags: ["CompanyIntegrations"],
    }),
    companyGetJiraProjects: builder.query<
      IApiResponse<IJiraProject[]>,
      { integrationId: number; search?: string }
    >({
      query: ({ integrationId, search }) => ({
        url: INTEGRATIONS.JiraProjects(integrationId),
        params: search ? { search } : undefined,
      }),
    }),
  }),
});

export const {
  useCompanyGetIntegrationsQuery,
  useCompanyGetAvailableIntegrationsQuery,
  useLazyCompanyGetJiraAuthorizeUrlQuery,
  useCompanyConnectJiraApiTokenMutation,
  useCompanyVerifyIntegrationMutation,
  useCompanyDeleteIntegrationMutation,
  useCompanyGetJiraProjectsQuery,
} = companyIntegrationsApi;
